/*
 * What the user is looking at, derived from the Backstage route.
 *
 * This is the whole point of embedding Guide in the host application rather than running it
 * beside one: a user on an entity page who says "this component" must be understood without
 * having to name it. Guide resolves "this" from the context the host hands over, so the host
 * has to know what the page is about.
 *
 * Pure and URL-only on purpose. `AppRootElementBlueprint` renders outside the app layout, so
 * nothing here may depend on being inside the catalog's React tree or its router.
 */

export interface GuidePageContext {
  /** A short human sentence. The agent reads this, so it is prose, not an identifier dump. */
  readonly description: string;
  readonly value: string;
}

/** Backstage entity routes are `/catalog/:namespace/:kind/:name`. */
const ENTITY_ROUTE = /^\/catalog\/([^/]+)\/([^/]+)\/([^/]+)/;
/** TechDocs routes are `/docs/:namespace/:kind/:name`. */
const DOCS_ROUTE = /^\/docs\/([^/]+)\/([^/]+)\/([^/]+)/;

/**
 * Routes with no entity in them, named the way a person would describe the page.
 *
 * The StackSchematic product pages are listed alongside Backstage's own, so a user on `/discovery`
 * or `/recover` who asks "what am I looking at?" gets a real answer up front rather than the bare
 * "the page at /discovery" — which forced Guide to infer the page from whichever tool it happened
 * to call. `/catalog-graph` must precede `/catalog`, and the StackSchematic routes are distinct
 * prefixes, so order among them does not matter.
 */
const NAMED_ROUTES: readonly (readonly [RegExp, string])[] = [
  [/^\/catalog-graph/, 'the catalog graph'],
  [/^\/catalog\/?$/, 'the catalog list of all entities'],
  [/^\/docs\/?$/, 'the documentation index'],
  [/^\/search/, 'search results'],
  [/^\/create/, 'the software templates page'],
  [/^\/settings/, 'their user settings'],
  // StackSchematic's own product surfaces.
  [/^\/agents/, "the discovery agents page — StackSchematic's fleet of vantage points and what they found"],
  [/^\/discovery/, 'the Discovery page — the estate the fleet found, what has witnessed each thing at runtime, and how each role was arrived at'],
  [/^\/connect/, 'the Connect page — which evidence sources are delivering'],
  [/^\/blueprint/, 'the Blueprint page — the estate organized by application'],
  [/^\/recover/, 'StackSchematic Recover — recovery documentation, gap analysis and planning'],
  [/^\/?$/, 'the catalog home page'],
];

function describeRoute(pathname: string): string {
  for (const [pattern, description] of NAMED_ROUTES) {
    if (pattern.test(pathname)) return description;
  }
  return `the page at ${pathname}`;
}

/**
 * Context for a Backstage location.
 *
 * The entity reference is given in Backstage's own `kind:namespace/name` form, because that is
 * what the catalog uses and what a StackSchematic tool would be given to look one up. Decoded,
 * since a name can legitimately be percent-encoded in the URL.
 */
export function contextForLocation(pathname: string): GuidePageContext[] {
  const entity = ENTITY_ROUTE.exec(pathname) ?? DOCS_ROUTE.exec(pathname);

  if (!entity) {
    return [
      {
        description: 'The page the user is currently viewing',
        value: describeRoute(pathname),
      },
    ];
  }

  const [, namespace, kind, name] = entity;
  const ref = `${decodeURIComponent(kind!).toLowerCase()}:${decodeURIComponent(namespace!)}/${decodeURIComponent(name!)}`;
  const docs = pathname.startsWith('/docs/');

  return [
    {
      description: 'The page the user is currently viewing',
      value: docs ? `the documentation for ${ref}` : `the catalog page for ${ref}`,
    },
    {
      description:
        "The entity currently in focus. When the user says 'this component', 'this " +
        "application' or 'this system', they mean this one",
      value: ref,
    },
  ];
}
